import { jsToFormData, paginatedTransform, rawTransform } from 'src/services/skuit/utils'
import {
  Attachment,
  CustomField,
  Flag,
  PaginatedResponse,
  ParcelOrder,
  ParcelOrderBarcodedUnit,
  ParcelOrderUnit,
  ParcelShipment,
  ParcelShipmentBarcodedUnit,
  ParcelShipmentUnit,
  TruckloadSMShipment,
} from '../../api/models'

import { API, CustomAxiosConfig } from './types'
import { ExportFilters } from '../skuit/types'
import fileDownload from 'js-file-download'

export interface ExportProps {
  fields: string[],
  exportFilters: ExportFilters,
}

class OrderCustomFields {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<CustomField>> => {
    const response = await this.api.get('/parcel/orders/custom-fields', config)
    return paginatedTransform(CustomField)(response.data)
  }

  create = async (data: any = {}, config: CustomAxiosConfig = {}): Promise<CustomField> => {
    const response = await this.api.post('/parcel/orders/custom-fields', data, config)
    return rawTransform(CustomField)(response.data)
  }
}

class OrderCustomFieldValues {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<CustomField>> => {
    const response = await this.api.get('/parcel/order-custom-field-values', config)
    return paginatedTransform(CustomField)(response.data)
  }

  create = async (data: any = {}, config: CustomAxiosConfig = {}): Promise<CustomField> => {
    const response = await this.api.post('/parcel/order-custom-field-values', data, config)
    return rawTransform(CustomField)(response.data)
  }
}

class OrderFlags {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Flag>> => {
    const response = await this.api.get('/parcel/orders/flags', config)
    return paginatedTransform(Flag)(response.data)
  }

  create = async (data: any, config: CustomAxiosConfig = {}): Promise<Flag> => {
    const response = await this.api.post('/parcel/orders/flags', data, config)
    return rawTransform(Flag)(response.data)
  }
}

class OrderBarcodedUnits {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<ParcelOrderBarcodedUnit>> => {
    const response = await this.api.get('/parcel/order-barcoded-units', config)
    return paginatedTransform(ParcelOrderBarcodedUnit)(response.data)
  }
}

class OrderAttachments {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (guid: string, config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Attachment>> => {
    const response = await this.api.get(`/parcel/orders/${guid}/attachments`, config)
    return paginatedTransform(Attachment)(response.data)
  }

  create = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<any> => {
    const axiosConfig = {
      ...config,
      headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
    }
    return await this.api.post(
      `/parcel/orders/${guid}/attachments`,
      jsToFormData(data),
      axiosConfig
    )
  }
}

class OrderUnits {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<ParcelOrderUnit>> => {
    const response = await this.api.get('/parcel/order-units', config)
    return paginatedTransform(ParcelOrderUnit)(response.data)
  }
}

class Orders {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<ParcelOrder>> => {
    const response = await this.api.get('/parcel/orders', config)
    return paginatedTransform(ParcelOrder)(response.data)
  }

  retrieve = async (guid: string, config: CustomAxiosConfig = {}): Promise<ParcelOrder> => {
    const response = await this.api.get(`/parcel/orders/${guid}`, config)
    return rawTransform(ParcelOrder)(response.data)
  }

  create = async (data: any, config: CustomAxiosConfig = {}): Promise<ParcelOrder> => {
    const response = await this.api.post('/parcel/orders', data, config)
    return rawTransform(ParcelOrder)(response.data)
  }

  patch = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<ParcelOrder> => {
    const response = await this.api.patch(`/parcel/orders/${guid}`, data, config)
    return rawTransform(ParcelOrder)(response.data)
  }

  skuAvailability = async (data: any, config: CustomAxiosConfig = {}) => {
    config.headers = { 'Content-Type': 'multipart/form-data', ...config.headers }
    const response = await this.api.post('/parcel/orders/sku-availability/csv', data, config)
    return response
  }

  palletAvailability = async (data: any, config: CustomAxiosConfig = {}) => {
    config.headers = { 'Content-Type': 'multipart/form-data', ...config.headers }
    const response = await this.api.post('/parcel/orders/pallet-availability/csv', data, config)
    return response
  }

  export = async ({ fields, exportFilters }: ExportProps) => {
    const guid: string | undefined = exportFilters?.guid
    const status: string | undefined = exportFilters?.status
    const params = { fields: fields.join(','), products: guid, status: status}
    const response = await this.api.axiosInstance({
      url: 'parcel/order-units/export',
      params,
      responseType: 'blob'
    })
    fileDownload(response.data, (guid ? `parcelOrderUnits_product_${guid}.csv` : 'parcelOrderUnits.csv')) 
  }

  get customFields() {
    const api = new OrderCustomFields(this.api)
    Object.defineProperty(this, 'customFields', { value: api })
    return api
  }

  get customFieldValues() {
    const api = new OrderCustomFieldValues(this.api)
    Object.defineProperty(this, 'customFieldValues', { value: api })
    return api
  }

  get attachments() {
    const api = new OrderAttachments(this.api)
    Object.defineProperty(this, 'attachments', { value: api })
    return api
  }

  get units() {
    const api = new OrderUnits(this.api)
    Object.defineProperty(this, 'units', { value: api })
    return api
  }

  get barcodedUnits() {
    const api = new OrderBarcodedUnits(this.api)
    Object.defineProperty(this, 'barcodedUnits', { value: api })
    return api
  }

  get flags() {
    const api = new OrderFlags(this.api)
    Object.defineProperty(this, 'flags', { value: api })
    return api
  }
}

class ShipmentAttachments {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (guid: string, config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Attachment>> => {
    const response = await this.api.get(`/parcel/shipments/${guid}/attachments`, config)
    return paginatedTransform(Attachment)(response.data)
  }

  create = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<any> => {
    const axiosConfig = {
      ...config,
      headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
    }
    return await this.api.post(
      `/parcel/shipments/${guid}/attachments`,
      jsToFormData(data),
      axiosConfig
    )
  }
}

class ShipmentBarcodedUnits {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (
    config: CustomAxiosConfig = {}
  ): Promise<PaginatedResponse<ParcelShipmentBarcodedUnit>> => {
    const response = await this.api.get('/parcel/shipment-barcoded-units', config)
    return paginatedTransform(ParcelShipmentBarcodedUnit)(response.data)
  }
}

class ShipmentUnits {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<ParcelShipmentUnit>> => {
    const response = await this.api.get('/parcel/shipment-units', config)
    return paginatedTransform(ParcelShipmentUnit)(response.data)
  }
}

class Shipments {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<ParcelShipment>> => {
    config.params = { ...config.params }
    const response = await this.api.get('/parcel/shipments', config)
    return paginatedTransform(ParcelShipment)(response.data)
  }

  create = async (data: any, config: CustomAxiosConfig = {}): Promise<ParcelShipment> => {
    const response = await this.api.post('/parcel/shipments', data, config)
    return rawTransform(ParcelShipment)(response.data)
  }

  patch = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<ParcelShipment> => {
    const response = await this.api.patch(`/parcel/shipments/${guid}`, data, config)
    return rawTransform(ParcelShipment)(response.data)
  }

  retrieve = async (guid: string, config: CustomAxiosConfig = {}): Promise<ParcelShipment> => {
    const response = await this.api.get(`/parcel/shipments/${guid}`, config)
    return rawTransform(ParcelShipment)(response.data)
  }

  export = async ({ fields }: ExportProps) => {
    const params = { fields: fields.join(',') }
    const response = await this.api.axiosInstance({
      url: 'parcel/shipments/export',
      params,
      responseType: 'blob'
    })
    fileDownload(response.data, 'parcel_shipments.csv')
  }

  packingSlip = async (
    guid: string,
    config: CustomAxiosConfig = {}
  ): Promise<TruckloadSMShipment> => {
    const response = await this.api.get(`/parcel/shipments/${guid}/packing-slip`, config)
    return rawTransform(TruckloadSMShipment)(response.data)
  }

  get attachments() {
    const api = new ShipmentAttachments(this.api)
    Object.defineProperty(this, 'attachments', { value: api })
    return api
  }

  get barcodedUnits() {
    const api = new ShipmentBarcodedUnits(this.api)
    Object.defineProperty(this, 'barcodedUnits', { value: api })
    return api
  }

  get units() {
    const api = new ShipmentUnits(this.api)
    Object.defineProperty(this, 'units', { value: api })
    return api
  }
}

export class Parcel {
  api: API

  constructor(api: API) {
    this.api = api
  }

  get orders() {
    const api = new Orders(this.api)
    Object.defineProperty(this, 'orders', { value: api })
    return api
  }

  get shipments() {
    const api = new Shipments(this.api)
    Object.defineProperty(this, 'shipments', { value: api })
    return api
  }
}
